import * as React from "react";
import { Rating } from "./components/rating";
import { v4 } from "uuid";
import { Tag } from "./components/tag";

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rating: 1,
      journal: "",
      tags: [],
      dayFor: undefined,
      lastSubmit: new Date(),
      isSubmitting: false,
      isListening: false,
      entries: [],
    };
  }

  componentDidMount() {
    fetch(`https://sljbeuyrgj.execute-api.us-east-1.amazonaws.com/journal`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        data.sort((a, b) => b.timestamp - a.timestamp);
        this.setState({ lastSubmit: new Date(data[0].timestamp), entries: data });
      });
  }

  componentWillUnmount() {
    if (this.recognition) this.recognition.stop();
  }

  exportCsv(range) {
    const { entries } = this.state;
    if (!entries || entries.length === 0) return;

    const cutoff =
      range === "90d" ? Date.now() - 90 * 24 * 60 * 60 * 1000 : null;
    const filtered = cutoff
      ? entries.filter((e) => e.timestamp >= cutoff)
      : entries;

    const escape = (v) => {
      if (v === undefined || v === null) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };

    const header = ["timestamp", "date", "rating", "journal", "dayFor", "tags"];
    const rows = filtered.map((e) => [
      e.timestamp,
      new Date(e.timestamp).toISOString(),
      e.rating,
      e.journal,
      e.dayFor,
      (e.tags || []).map((t) => `${t.name}:${t.value}`).join("|"),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map(escape).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `journal-export-${range}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async submit() {
    const { rating, journal, tags, dayFor } = this.state;
    this.setState({ isSubmitting: true });
    try {
      await fetch(
        `https://sljbeuyrgj.execute-api.us-east-1.amazonaws.com/writeJournal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ rating, journal, id: v4(), tags, dayFor }),
        }
      );
      this.setState({ rating: 3, journal: "", tags: [] });
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  toggleDictation() {
    if (this.state.isListening) {
      this.recognition.stop();
      this.setState({ isListening: false });
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = false;
    this.recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        this.setState((prev) => ({
          journal: prev.journal ? prev.journal + " " + transcript : transcript,
        }));
      }
    };
    this.recognition.onend = () => this.setState({ isListening: false });
    this.recognition.onerror = () => this.setState({ isListening: false });
    this.recognition.start();
    this.setState({ isListening: true });
  }

  renderAuthHeader() {
    const { authUser, onLogout } = this.props;
    if (!authUser) return null;
    return (
      <div className="row">
        <div className="col-12 auth-header">
          <span className="auth-email">{authUser.email}</span>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => this.exportCsv("all")}
            disabled={this.state.entries.length === 0}
          >
            Export All
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => this.exportCsv("90d")}
            disabled={this.state.entries.length === 0}
          >
            Export Last 90 Days
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  setRating(rating) {
    this.setState({ rating });
  }

  setJournal(event) {
    this.setState({ journal: event.target.value });
  }

  addTag(name, value) {
    const newTags = [...this.state.tags, { name, value, id: v4() }];
    this.setState({ tags: newTags });
  }

  setDate(event) {
    this.setState({ dayFor: event.target.value });
  }

  render() {
    const { isSubmitting, isListening } = this.state;
    const dictationSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    return (
      <div className="container" style={{ position: "relative" }}>
        {isSubmitting && (
          <div className="loader" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}
        {this.renderAuthHeader()}
        <div className="row">
          <div className="col-12">
            <div className="header-text">
              Last Submit Date: {`${this.state.lastSubmit.toDateString()}`}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 offset-md-2">
            <Rating
              onChange={this.setRating.bind(this)}
              rating={this.state.rating}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 offset-md-2">
            <textarea
              className="main-editor"
              onChange={this.setJournal.bind(this)}
              value={this.state.journal}
            ></textarea>
            {dictationSupported && (
              <button
                className={`btn btn-sm mt-1 ${isListening ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={this.toggleDictation.bind(this)}
              >
                {isListening ? "⏹ Stop dictating" : "🎤 Dictate"}
              </button>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 offset-md-2">
            <Tag
              addTag={(name, value) => this.addTag(name, value)}
              tags={this.state.tags}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 offset-md-2">
            {this.state.tags.map((tag) => (
              <div key={tag.value}>
                {tag.name} - {tag.value}
              </div>
            ))}
          </div>
        </div>
        <div className="row add-padding">
          <div className="col-12 col-md-4 offset-md-2">
            Change Day:{" "}
            <input
              type="date"
              className="form-control"
              onChange={(e) => this.setDate(e)}
              value={this.state.dayFor}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 offset-md-2">
            <button
              className="btn btn-success btn-block submit-button"
              onClick={this.submit.bind(this)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: "8px" }}
                  />
                  Submitting…
                </>
              ) : (
                "Submit Entry"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default App;
